/** The parts Season's Rooms, a room and a session report share: the poster, the link, a draft's four stops, the section rows. */
import type { ReactNode } from "react";
import type { HostRoomRow } from "../../../shared/talk";
import type { WebinarDetail, WebinarLive } from "../../../shared/types";
import { timeOf } from "../../board/series";
import { GroupedList, ListRow } from "../../components/season/GroupedList";
import { Picture } from "../../components/season/Picture";
import { Icon } from "../../components/ui/Icon";
import { Pill } from "../../components/ui/Pill";
import { Seg } from "../../components/ui/Seg";
import { useToast } from "../../components/ui/Toast";
import { placesPath } from "../../talk/places";
import { lastRun, shortDay } from "./liveWords";
import { copyRoomLink } from "./RoomActions";
import { talkRowLine } from "./RoomTalk";
import { KIND_TONE, kindOf, lengthLabel, type RoomTab, tabPath, tabsFor } from "./roomWords";
import { onHere, useRoomAutomations } from "./useFollowups";

const n = (v: number) => v.toLocaleString("en-US");

type PictureOf = Parameters<typeof kindOf>[0] & Parameters<typeof lengthLabel>[0] & { poster_url: string | null };

/** A room's picture at the top of its page and of its card: its frame, else a tile, with its kind and length on it. */
export function RoomPicture({ room }: { room: PictureOf }) {
  const kind = kindOf(room);
  const draft = kind === "Draft";
  const length = draft ? "" : lengthLabel(room);
  return (
    <Picture
      className={draft || !room.poster_url ? "rm-pic rm-tile" : "rm-pic"}
      size="lg"
      src={draft ? null : room.poster_url}
      fallback={draft ? "pencil" : "play"}
      badge={<Pill tone={KIND_TONE[kind]}>{kind}</Pill>}
      {...(length ? { duration: length } : {})}
    />
  );
}

/** The room's public link with a copy button. */
export function LinkRow({ url }: { url: string }) {
  const toast = useToast();
  return (
    <div className="rm-link">
      <Icon name="globe" />
      <span>{url.replace(/^https?:\/\//, "")}</span>
      <button type="button" className="k-ib" aria-label="Copy link" onClick={() => void copyRoomLink(url, toast)}>
        <Icon name="copy" />
      </button>
    </div>
  );
}

/** A filter whose choices carry a count, drawn as the kit's switch. */
export function CountSwitch<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: ReadonlyArray<{ value: T; label: string; count: number }>;
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="k-swb">
      <Seg className="k-seg rm-f" label={label} value={value} onChange={onChange} options={options} />
    </div>
  );
}

export const SECTIONS: Record<RoomTab, { icon: "users" | "live" | "cal" | "globe" | "zap"; label: string }> = {
  audience: { icon: "users", label: "Audience" },
  live: { icon: "live", label: "Last live" },
  sessions: { icon: "cal", label: "Sessions" },
  page: { icon: "globe", label: "Page and offer" },
  followups: { icon: "zap", label: "Follow-ups" },
};

/** A section's address. Why audience has its own: on a phone each section is a screen of its own under the room. */
export function sectionPath(id: string, tab: RoomTab): string {
  return tab === "audience" ? `/rooms/${id}/audience` : tabPath(id, tab);
}

/** Questions the last live left that no one has answered. */
function questionsWaiting(live: WebinarLive | null): number {
  return live ? live.questions.filter((q) => q.answered === null).length : 0;
}

/** "Next Tue 3 Nov, 6:00 PM", and what it is about. */
function nextSession(detail: WebinarDetail): ReactNode {
  const s = detail.live?.series?.sessions.find((x) => x.state === "scheduled" || x.state === "live");
  if (!s) return "";
  const when = `Next ${shortDay(s.startsAt, s.timezone)}, ${timeOf(s.startsAt, s.timezone)}`;
  if (s.topic) return `${when}, ${s.topic}`;
  return (
    <>
      {when}. <em className="k-hot">Set its topic</em>
    </>
  );
}

/** The five sections as rows, each with one line about where it stands. `on` marks the open one beside them. */
export function SectionRows({ detail, lastLive, on, talk }: { detail: WebinarDetail; lastLive: WebinarLive | null; on?: RoomTab; talk?: HostRoomRow | null }) {
  const { autos } = useRoomAutomations(detail.id);
  const ran = detail.live?.series ? lastRun(detail.live.series.sessions) : null;
  const waiting = questionsWaiting(lastLive);
  const line = (key: RoomTab): ReactNode => {
    if (key === "audience") return `${n(detail.registered_here)} registered, ${n(detail.chain.came)} came`;
    if (key === "live") {
      if (!lastLive) return "";
      return (
        <>
          {n(lastLive.cameLive)} came,{" "}
          {waiting ? (
            <em className="k-hot">
              {n(waiting)} {waiting === 1 ? "question" : "questions"} waiting
            </em>
          ) : (
            "all questions answered"
          )}
        </>
      );
    }
    if (key === "sessions") return nextSession(detail);
    if (key === "page") return detail.offer?.label ? `The offer: ${detail.offer.label}` : "No offer yet";
    return autos ? `${autos.filter((a) => onHere(a, detail.id)).length} on for this room` : "";
  };
  return (
    <GroupedList className="rm-secs">
      {tabsFor(detail).flatMap((t) => {
        const s = SECTIONS[t.key];
        const row = (
          <ListRow
            key={t.key}
            icon={s.icon}
            tone={t.key === "live" && waiting ? "wine" : undefined}
            title={s.label}
            sub={line(t.key)}
            value={t.key === "live" && ran ? shortDay(ran.startsAt, ran.timezone) : undefined}
            on={on === t.key}
            to={sectionPath(detail.id, t.key)}
          />
        );
        // Why under Audience: it is the room's people, and the room's conversations are with them.
        return t.key === "audience" && talk
          ? [row, <ListRow key="talk" icon="chat" tone="wine" title="Conversations" sub={talkRowLine(talk)} to={placesPath(detail.id)} />]
          : [row];
      })}
    </GroupedList>
  );
}
